import { FC, SyntheticEvent, useState } from 'react'
import { IconButton, OptionTemplate, Tooltip } from '@cargomart/ui'
import { useNavigate } from '@tanstack/react-router'
import IconAttach from 'assets/attach.svg?react'
import IconDetach from 'assets/detach.svg?react'
import IconPlus from 'assets/plus.svg?react'
import cn from 'classnames'
import { IconTooltipHelp, MODAL_IDS, ModalItem, useModal } from 'components'
import { useDebounce, useIsMobile } from 'hooks'
import CustomerAddModal from 'modules/customer/components/CustomerAddModal/CustomerAddModal'
import { getCustomerInnKpp, getCustomerName } from 'modules/customer/helpers'
import { customerDetailRoute } from 'modules/customer/routes'
import { TCustomer } from 'modules/customer/types'
import IconArrowLeft from 'remixicon/icons/Arrows/arrow-left-line.svg?react'
import { Button } from 'src/shared/components'
import { ListRow } from 'src/tailwind-components/extends/List'
import { ModalFooter, ModalHeader } from 'src/tailwind-components/extends/Modal'
import { PlaceholderCell } from 'src/tailwind-components/extends/PlaceholderCell'
import Search from 'src/tailwind-components/extends/Search/Search'
import { CustomerAddLocationKeys } from 'store/customerAdd/constants'

import { ContactCustomersTableProps } from './types'

import s from './styles.module.less'

const STORE_KEY = CustomerAddLocationKeys.ContactDetail

const ContactCustomersTable: FC<ContactCustomersTableProps> = (props) => {
  const {
    contactId,
    isAccessAddCustomer,
    isAccessDeleteCustomer,
    attachedCustomers,
    isAttachedCustomersFetching,
    customers,
    refetchAttachedCustomers,
    isCustomerListLoading,
    isCustomerListFetching,
    refetchCustomers,
    attachCustomerByContactId,
    detachCustomerByContactId
  } = props
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const { closeModal, openModal } = useModal()
  const skeleton = [...new Array(1)].map((_, i) => <PlaceholderCell key={i} className={cn(s.skeleton)} height={52} />)

  const toolTipText =
    'Контакт может выступать представителем одного или нескольких юридических лиц. Свяжите его с организацией из списка ваших контрагентов.'

  const noCustomersText = 'Нет связанных контрагентов'
  const empty = 'Контрагенты не найдены'

  const [showAttachBtn, setShowAttachBtn] = useState(true)

  const handleOpenModalDetail = (customer: TCustomer) => {
    navigate({ to: customerDetailRoute.to, params: { id: customer.id } })
  }

  const handleShowCustomerList = () => {
    setShowAttachBtn(false)
    refetchCustomers && refetchCustomers()
  }

  const searchPlaceholder = 'Название организации'

  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')

  const debouncedSearchCustomer = useDebounce(debouncedSearchTerm, 500)
  const [filteredSearchTerm, setFilteredSearchTerm] = useState(debouncedSearchCustomer)
  const handleSearchCustomer = (search: string) => {
    setDebouncedSearchTerm(search)
    setFilteredSearchTerm(search)
  }

  const handleHideCustomerList = () => {
    handleSearchCustomer('')
    setShowAttachBtn(true)
  }

  const filteredCustomerList = () => {
    if (!filteredSearchTerm) {
      return customers
    }

    // @ts-ignore
    return customers.filter((item) => item.name.toLowerCase().includes(filteredSearchTerm.toLowerCase()))
  }

  const handleOpenCustomerAddModal = () => {
    setShowAttachBtn(true)
    openModal(MODAL_IDS.AddCustomerList)
  }

  const handleCloseCustomerAddModal = () => {
    closeModal(MODAL_IDS.AddCustomerList)
  }

  const handleAttach = (contactId: string, customerId: string) => {
    attachCustomerByContactId &&
      // @ts-ignore
      attachCustomerByContactId({ contactId, id: customerId })
        .unwrap()
        .then(() => {
          handleHideCustomerList()
          refetchAttachedCustomers && refetchAttachedCustomers()
        })
  }

  const handleDetach = (e: SyntheticEvent, contactId: string, customerId: string) => {
    e.stopPropagation()
    detachCustomerByContactId &&
      // @ts-ignore
      detachCustomerByContactId({ contactId, id: customerId })
        .unwrap()
        .then(() => {
          refetchAttachedCustomers && refetchAttachedCustomers()
        })
  }

  return (
    <>
      <div className={s.table}>
        <div className={s.tableHeader}>
          <h2>Организация</h2>
          <IconTooltipHelp placement="right">{toolTipText}</IconTooltipHelp>
        </div>
        <div className={s.tableBody} data-test-id="contacts-page--contact-detail--attached-customer-list">
          {/* eslint-disable-next-line */}
          {attachedCustomers?.length ? (
            attachedCustomers?.map((attachedCustomer) =>
              !isAttachedCustomersFetching ? (
                <div key={attachedCustomer.id} data-test-id="company-item">
                  <OptionTemplate
                    className="items-center pl-4"
                    description={getCustomerInnKpp(attachedCustomer)}
                    onClick={() => handleOpenModalDetail(attachedCustomer)}
                    title={getCustomerName(attachedCustomer)}
                    postfix={
                      isAccessDeleteCustomer ? (
                        <div data-test-id="company-detach-btn">
                          <Tooltip content="Открепить организацию" placement="right">
                            <div>
                              <IconDetach
                                containerSize="lg"
                                onClick={(e: SyntheticEvent) => handleDetach(e, contactId, attachedCustomer.id)}
                                size="xs"
                              />
                            </div>
                          </Tooltip>
                        </div>
                      ) : null
                    }
                  />
                </div>
              ) : (
                skeleton
              )
            )
          ) : (
            <div
              className={s.noCustomersText}
              data-test-id="contacts-page--contact-detail--empty-attached-customer-list"
            >
              {noCustomersText}
            </div>
          )}
        </div>
        <div className={cn(s.tableFooter, showAttachBtn ? '' : s.isActive)}>
          {isAccessAddCustomer && showAttachBtn ? (
            <Button
              className={s.attachBtn}
              onClick={handleShowCustomerList}
              size="nm"
              testId="contacts-page--contact-detail--customer-attach-btn"
              variant="text"
            >
              <IconAttach size="xs" />
              Привязать к организации
            </Button>
          ) : (
            <>
              {isMobile ? (
                <div className={s.attachBlockMobile}>
                  <ModalHeader className={s.detailHeader}>
                    <div className={cn(s.modalTitle, s.modalDetailTitle)}>
                      {!isCustomerListLoading ? (
                        <div className={s.mobileCloseTitle}>
                          <IconButton onClick={handleHideCustomerList}>
                            <IconArrowLeft containerSize="lg" size="sm" />
                          </IconButton>
                          Выберите организацию
                        </div>
                      ) : (
                        <PlaceholderCell height={32} />
                      )}
                    </div>
                  </ModalHeader>
                  <div className={s.attachBlock} style={{ zIndex: 2 }}>
                    <Search
                      isInDetailPage
                      handleSearch={handleSearchCustomer}
                      placeholder={searchPlaceholder}
                      searchQuery={filteredSearchTerm || ''}
                    />
                    <div className={s.customersBlock}>
                      <div className={s.customersList}>
                        {filteredCustomerList()?.length ? (
                          filteredCustomerList()?.map((customer) =>
                            !isCustomerListFetching ? (
                              <ListRow
                                key={customer.id}
                                className={cn(s.attachedCustomer, s.customers)}
                                onClick={() => handleAttach(contactId, customer.id)}
                              >
                                <div className={cn(s.customerName)}>{getCustomerName(customer)}</div>
                                <div className={cn(s.customerInnKpp)}>{getCustomerInnKpp(customer)}</div>
                              </ListRow>
                            ) : (
                              skeleton
                            )
                          )
                        ) : (
                          <div className={s.notFound}>{empty}</div>
                        )}
                      </div>
                    </div>
                  </div>
                  <ModalFooter className={s.detailFooter}>
                    <div className={s.customersAddBlock}>
                      <Button
                        className={cn(s.attachBtn, s.attachBtnMobile)}
                        onClick={handleOpenCustomerAddModal}
                        size="nm"
                        testId="contacts-page--contact-detail--create-customer-btn"
                        variant="text"
                      >
                        Создать нового контрагента
                      </Button>
                    </div>
                  </ModalFooter>
                </div>
              ) : (
                <>
                  <div className={s.attachContainer} onClick={handleHideCustomerList}></div> {/* eslint-disable-line */}
                  <div className={s.attachBlock} style={{ zIndex: 2 }}>
                    <Search
                      isInDetailPage
                      handleSearch={handleSearchCustomer}
                      placeholder={searchPlaceholder}
                      searchQuery={filteredSearchTerm || ''}
                    />
                    <div className={s.customersBlock}>
                      <div className={s.customersList}>
                        {filteredCustomerList()?.length ? (
                          filteredCustomerList()?.map((customer) =>
                            !isCustomerListFetching ? (
                              <ListRow
                                key={customer.id}
                                className={cn(s.attachedCustomer, s.customers)}
                                onClick={() => handleAttach(contactId, customer.id)}
                              >
                                <div className={cn(s.customerName)}>{getCustomerName(customer)}</div>
                                <div className={cn(s.customerInnKpp)}>{getCustomerInnKpp(customer)}</div>
                              </ListRow>
                            ) : (
                              skeleton
                            )
                          )
                        ) : (
                          <div className={s.notFound}>{empty}</div>
                        )}
                      </div>
                      <div className={s.line}></div> {/* eslint-disable-line */}
                      <div className={s.customersAddBlock}>
                        <Button
                          className={s.attachBtn}
                          onClick={handleOpenCustomerAddModal}
                          size="nm"
                          testId="contacts-page--contact-detail--create-customer-btn"
                          variant="text"
                        >
                          <IconPlus size="xs" />
                          Создать нового контрагента
                        </Button>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
      <ModalItem id={MODAL_IDS.AddCustomerList}>
        <CustomerAddModal
          contactId={contactId}
          handleAttach={handleAttach}
          onClose={handleCloseCustomerAddModal}
          refetch={refetchCustomers}
          storeKey={STORE_KEY}
        />
      </ModalItem>
    </>
  )
}

export default ContactCustomersTable
